import axios from "axios";

/**
 * Validate that a string contains only valid ASCII characters (0-255)
 */
function isValidHeaderValue(value: string | null): boolean {
  if (!value) return true;

  for (let i = 0; i < value.length; i++) {
    const charCode = value.charCodeAt(i);
    if (charCode > 255) {
      console.error(`Invalid character at index ${i}: charCode ${charCode} (character: ${value[i]})`);
      return false;
    }
  }
  return true;
}

/**
 * Clear corrupted authentication data and reload
 */
function clearCorruptedAuth() {
  console.warn("Detected corrupted authentication token - clearing and reloading");
  localStorage.removeItem('access_token');
  localStorage.removeItem('sudo_session_id');
  window.location.reload();
}

/**
 * Get the backend host from the current frontend host.
 * Supports two URL patterns:
 * 1. Production: global-admin.example.com -> api.example.com
 * 2. BitSwan deployments: {workspace}-global-admin-frontend-{stage}.{domain} -> {workspace}-django-{stage}.{domain}
 */
export function getBackendHost(currentHost: string): string {
  // BitSwan deployment pattern for global-admin-frontend
  const bitswanPattern = /^(.+)-global-admin-frontend(-[^.]+)?\.(.+)$/;
  const match = currentHost.match(bitswanPattern);

  if (match) {
    const workspace = match[1];
    const stage = match[2] || '';
    const domain = match[3];
    return `${workspace}-django${stage}.${domain}`;
  }

  // Also support old aoc-frontend pattern (for shared deployments)
  const aocPattern = /^(.+)-aoc-frontend(-[^.]+)?\.(.+)$/;
  const aocMatch = currentHost.match(aocPattern);

  if (aocMatch) {
    const workspace = aocMatch[1];
    const stage = aocMatch[2] || '';
    const domain = aocMatch[3];
    return `${workspace}-django${stage}.${domain}`;
  }

  // Production pattern: global-admin.example.com -> api.example.com
  return currentHost.replace(/^global-admin\./, 'api.').replace(/^aoc\./, 'api.');
}

export const authenticatedBitswanBackendInstance = async (
  headers: Record<string, string> = {},
) => {
  const apiToken = localStorage.getItem('access_token');
  const sudoSessionId = localStorage.getItem('sudo_session_id');

  // Validate token doesn't contain invalid characters
  if (apiToken && !isValidHeaderValue(apiToken)) {
    clearCorruptedAuth();
    throw new Error("Invalid authentication token detected");
  }

  if (sudoSessionId && !isValidHeaderValue(sudoSessionId)) {
    localStorage.removeItem('sudo_session_id');
  }

  const currentHost = window.location.hostname;
  const protocol = "https:";
  const backendHost = getBackendHost(currentHost);
  const baseURL = `${protocol}//${backendHost}/api/frontend`;

  if (!apiToken) {
    console.error("No access token available for API calls");
  }

  const requestHeaders: Record<string, string> = {
    Authorization: `Bearer ${apiToken}`,
    "Content-Type": "application/json",
    ...headers,
  };

  // Add sudo session header if in sudo mode
  if (sudoSessionId) {
    requestHeaders["X-Sudo-Session-Id"] = sudoSessionId;
  }

  return axios.create({
    baseURL: baseURL,
    headers: requestHeaders,
  });
};
